import { Component, h, Prop, State, Watch, Element } from '@stencil/core';

@Component({
  tag: 'signet-stepper',
  styleUrl: 'signet-stepper.scss',
  shadow: true,
})
export class CustomStepper {
  @Element() el!: HTMLElement;
  /**
   * The orientation of the stepper. Can be vertical or horizontal.
   * @example vertical 
   * @example horizontal
   */
  @Prop() orientation: 'vertical' | 'horizontal' = 'horizontal';
  /**
   * The steps of the stepper. Can be an array of strings or a JSON string.
   * @example ['Step 1', 'Step 2', 'Step 3']
   * @example '[{"label": "Step 1"}, {"label": "Step 2"}, {"label": "Step 3"}]'
   * @example '[{"label": "Step 1", "disabled": true}, {"label": "Step 2"}, {"label": "Step 3"}]'
   */
  @Prop() steps: string[] = ['Step 1', 'Step 2', 'Step 3', 'Step 4'];
  /**
   * The current step of the stepper. Can be a number or a string.
   * @example 0
   */
  @State() currentStep = 0;

  private _steps: string[];

  @Watch('steps')
  parseStepsProp(newValue: string[] | string) {
    if (typeof newValue === 'string') {
      this._steps = JSON.parse(newValue);
    } else {
      this._steps = newValue;
    }
  }

  componentWillLoad() {
    this.parseStepsProp(this.steps);
  }

  handleNext() {
    if (this.currentStep < this._steps.length - 1) {
      this.currentStep++;
      this.el.dispatchEvent(new CustomEvent('input', {
        bubbles: true,
        composed: true,
        detail: this.currentStep,
      }));
      this.el.dispatchEvent(new CustomEvent('update:modelValue', {
        bubbles: true,
        composed: true,
        detail: this.currentStep,
      }));
    }
  }

  handlePrevious() {
    if (this.currentStep > 0) {
      this.currentStep--;
      this.el.dispatchEvent(new CustomEvent('input', {
        bubbles: true,
        composed: true,
        detail: this.currentStep,
      }));
      this.el.dispatchEvent(new CustomEvent('update:modelValue', {
        bubbles: true,
        composed: true,
        detail: this.currentStep,
      }));
    }
  }

  render() {
    const isVertical = this.orientation === 'vertical';

    return (
      <div class={`stepper ${isVertical ? 'vertical' : 'horizontal'}`}>
        <div class="steps">
          {this._steps.map((step, index) => (
            <div class={`step ${this.currentStep === index ? 'active' : ''}`}>
              <div class="marker">{index + 1}</div>
              <div class="label">{step}</div>
              {index !== this._steps.length - 1 && <hr class="step-line" />}
            </div>
          ))}
        </div>
        <div class="content">
          <slot name={`step-${this.currentStep}`} />
        </div>
        <div class="navigation">
          <button
            class="btn-previous"
            onClick={() => this.handlePrevious()}
            disabled={this.currentStep === 0}
          >
            Previous
          </button>
          <button
            class="btn-next"
            onClick={() => this.handleNext()}
            disabled={this.currentStep === this._steps.length - 1}
          >
            Next
          </button>
        </div>
      </div>
    );
  }
}
